/**
 * Public reporter profile — /reporter/:userId
 * Shows badge, published-story counter and last 10 pieces so readers
 * can follow trusted community voices.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Feather, ArrowLeft, MapPin, Calendar, AlertCircle, Loader2 } from "lucide-react";
import { npApi } from "./api";
import ContributorBadge from "@/components/newspulse/ContributorBadge";

export default function NewsPulseReporterProfile() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    if (!userId) return;
    npApi.reporterProfile(userId).then(setData).catch((e) => setErr(e.message));
  }, [userId]);

  if (err) return (
    <div className="max-w-[900px] mx-auto px-4 py-16 text-center" data-testid="reporter-error">
      <AlertCircle className="w-8 h-8 text-rose-600 mx-auto mb-3" />
      <p className="text-sm text-slate-700">{err}</p>
      <Link to="/" className="text-sm text-blue-600 hover:text-blue-800 underline mt-4 inline-block">Back to home</Link>
    </div>
  );
  if (!data) return (
    <div className="max-w-[900px] mx-auto px-4 py-16 flex items-center justify-center gap-2 text-slate-500" data-testid="reporter-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading reporter…
    </div>
  );

  const badgeInitial = data.tier ? { approved_count: data.approved_count, tier: data.tier, next_tier: data.next_tier } : null;

  return (
    <div className="max-w-[960px] mx-auto px-4 lg:px-6 py-10" data-testid="reporter-profile-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> The News Pulse
      </Link>

      {/* Hero */}
      <div className="flex flex-wrap items-start gap-5 mb-8">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-100 border-2 border-white shadow-sm shrink-0">
          {data.avatar_url ? (
            <img src={data.avatar_url} alt={data.display_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-slate-400">
              {(data.display_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-1">Community Reporter</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-tight"
              style={{ fontFamily: "'Lora', 'Georgia', serif" }}
              data-testid="reporter-display-name">
            {data.display_name}
          </h1>
          {data.handle && <div className="text-sm text-slate-500 mt-1">@{data.handle}</div>}
          {data.bio && <p className="text-sm text-slate-600 mt-3 max-w-xl">{data.bio}</p>}
          <div className="mt-4">
            <ContributorBadge userId={userId} shape="widget" initial={badgeInitial} />
          </div>
        </div>
      </div>

      {/* Stories */}
      <h2 className="text-lg md:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"
          style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
        <Feather className="w-4 h-4 text-blue-600" />
        Recent stories ({data.approved_count})
      </h2>
      {data.stories.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-500"
             data-testid="reporter-empty">
          Nothing published yet. Check back soon.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="reporter-stories">
          {data.stories.map((s) => (
            <li key={s.event_id} className="rounded-2xl border border-slate-200 hover:border-blue-200 hover:shadow-sm transition-all"
                data-testid={`reporter-story-${s.event_id}`}>
              <Link to={`/event/${s.event_id}`} className="flex gap-4 p-4">
                {s.image_url && (
                  <img src={s.image_url} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-blue-600 mb-1">
                    {(s.category || "community").replace(/_/g, " ")}
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1 line-clamp-2">{s.headline}</h3>
                  {s.summary && <p className="text-sm text-slate-600 line-clamp-2">{s.summary}</p>}
                  <div className="text-[11px] text-slate-500 mt-2 flex flex-wrap gap-3">
                    {s.neighborhood && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.neighborhood}</span>}
                    {s.published_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{s.published_at.slice(0, 10)}</span>}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
